import React, { useCallback, useMemo, useRef } from 'react';
import cn from 'clsx';
import { enhanceImageUrl, replaceSpecialChars, removeSpecialCharacters, createElementKey } from 'utils';
import { Banner, useModelJsonSelector } from 'modules/model-json';
import {
  Button,
  CategoryCardHeader,
  CategoryCardMedia,
  CategoryCardText,
  CategoryCardWithGAPromoClick,
  LinkWithGAPromoClick,
  SectionSubTitle,
  SectionTitle,
  SwiperSlide,
  SwiperWithNavigation,
} from 'common/components';
import { useIsInViewport, usePromoViewEvent } from 'common/hooks';

import { analyticsUtils, ClickButtonElementName } from 'utils/analytics-utils';
import {
  firstBannerButton,
  firstBannerTitle,
  firstBannerDescription,
  firstSwiperSlide,
  headerSection,
  headerSubTitle,
  headerSwiperLink,
  headerSwiperSlide,
  headerTitle,
  mobileFirstBanner,
  swiperImage,
} from './header-section-styles';

const FirstBanner = ({ title, text, image }: Banner) => (
  <>
    <CategoryCardHeader
      className={firstBannerTitle}
      title={replaceSpecialChars(title)}
      size={false}
      variant="h3"
      as="h3"
    />
    {text && <CategoryCardText text={text} className={firstBannerDescription} />}
    <Button className={firstBannerButton} size="m" variant="outline" adaptive={false}>
      К покупкам
    </Button>
    <CategoryCardMedia
      alt={title}
      className={swiperImage}
      height={240}
      src={enhanceImageUrl(image)}
      title={title}
      width={220}
    />
  </>
);

/**
 * Вью хедер секции
 */
export const HeaderSection = () => {
  const { headerBanners } = useModelJsonSelector();

  const bannerRefs = useRef<HTMLAnchorElement[]>([]);
  const mobileFirstBannerRef = useRef<HTMLDivElement | null>(null);
  const isMobileFirstBannerVisible = useIsInViewport(mobileFirstBannerRef);

  const promotions = useMemo(
    () =>
      headerBanners
        ? headerBanners.map((promotion: Banner, index: number) => ({
            name: 'sad_catalogTopBanner',
            creative: `sad_${removeSpecialCharacters(promotion.title)}`,
            position: (index + 1).toString(),
          }))
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [headerBanners, isMobileFirstBannerVisible]
  );

  usePromoViewEvent(bannerRefs, 'landingSad', promotions);

  /**
   * Обработчик нажатия кнопки "Следующий слайд"
   */
  const handleNextSlideBtnClick = useCallback(() => {
    analyticsUtils.clickButtonEvent(
      'landingSad',
      ClickButtonElementName.SwitchForward,
      'sad_catalogTopBanner'
    );
  }, []);

  /**
   * Обработчик нажатия кнопки "Предыдущий слайд"
   */
  const handlePrevSlideBtnClick = useCallback(() => {
    analyticsUtils.clickButtonEvent(
      'landingSad',
      ClickButtonElementName.SwitchBackward,
      'sad_catalogTopBanner'
    );
  }, []);

  if (!headerBanners || headerBanners.length === 0) {
    return null;
  }

  return (
    <section className={headerSection}>
      <SectionTitle as="h1" className={headerTitle} variant="display">
        Сад и огород
      </SectionTitle>
      <SectionSubTitle adaptiveMargin as="h2" className={headerSubTitle} margin="s" variant="h2">
        Удачное предложение для&nbsp;работы и&nbsp;отдыха в&nbsp;саду
      </SectionSubTitle>

      <div className={mobileFirstBanner} data-position={0} ref={mobileFirstBannerRef}>
        <LinkWithGAPromoClick
          bannerRefs={bannerRefs}
          className={headerSwiperLink}
          elementBlock="landingSad"
          position={0}
          promotion={promotions[0]}
          href={headerBanners[0].url}
          title={headerBanners[0].button}>
          <FirstBanner {...headerBanners[0]} />
        </LinkWithGAPromoClick>
      </div>
      <SwiperWithNavigation
        onSlideNextTransitionEnd={handleNextSlideBtnClick}
        onSlidePrevTransitionEnd={handlePrevSlideBtnClick}
        slidesPerView="auto">
        {headerBanners.map(({ url, button, title, image, ...rest }: Banner, index: number) => (
          <SwiperSlide
            className={cn(headerSwiperSlide, { [firstSwiperSlide]: index === 0 })}
            key={createElementKey('headerCarousel', index)}>
            <CategoryCardWithGAPromoClick
              className={headerSwiperLink}
              elementBlock="landingSad"
              refCb={(el: HTMLAnchorElement | null) => {
                if (el) {
                  if (index === 0 && isMobileFirstBannerVisible) {
                    return;
                  }
                  bannerRefs.current[index] = el;
                }
              }}
              position={index}
              promotion={promotions[index]}
              href={url}
              title={button}>
              {index === 0 ? (
                <FirstBanner url={url} button={button} title={title} image={image} {...rest} />
              ) : (
                <>
                  <CategoryCardHeader title={replaceSpecialChars(title)} />
                  <CategoryCardMedia
                    alt={title}
                    className={swiperImage}
                    height={118}
                    src={enhanceImageUrl(image)}
                    title={title}
                    width={118}
                  />
                </>
              )}
            </CategoryCardWithGAPromoClick>
          </SwiperSlide>
        ))}
      </SwiperWithNavigation>
    </section>
  );
};
